/**
 * Reportes — agregados por cliente/proveedor en tallos Y valores,
 * más devoluciones e inventario. Todo filtrable por periodo (from/to).
 *
 * Por petición del cliente:
 *  - Ventas por cliente → tallos y USD.
 *  - Compras por proveedor → tallos, USD y COP (la moneda se elige por OC).
 *  - Devoluciones de compra (por proveedor) y de venta (por cliente).
 *  - Inventario actual o snapshot en una fecha específica.
 *  - Selector de periodo (from/to) para todos los reportes excepto
 *    inventario que tiene su propio selector "Al" (fecha).
 */
import { useMemo, useState } from 'react'
import { BarChart3 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Card, CardContent } from '@/components/ui/Card'
import { useApi } from '@/lib/useApi'
import { fmtMoney } from '@/lib/utils'

const fmtQty = (n) => Number(n || 0).toLocaleString('es-CO')

const REPORT_TABS = [
  { id: 'sales', label: 'Ventas por cliente' },
  { id: 'purchases', label: 'Compras por proveedor' },
  { id: 'returns', label: 'Devoluciones' },
  { id: 'inventory', label: 'Inventario' },
]

export default function ReportsPage() {
  const [tab, setTab] = useState('sales')
  const today = new Date().toISOString().slice(0, 10)
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10)
  const [from, setFrom] = useState(monthAgo)
  const [to, setTo] = useState(today)
  const [snapshotAt, setSnapshotAt] = useState('')

  const qs = useMemo(() => {
    const p = new URLSearchParams()
    if (from) p.set('from', from)
    if (to) p.set('to', to)
    return p.toString() ? `?${p.toString()}` : ''
  }, [from, to])

  const sales = useApi(tab === 'sales' ? `/reports/sales${qs}` : null, {
    enabled: tab === 'sales',
  })
  const purchases = useApi(
    tab === 'purchases' ? `/reports/purchases${qs}` : null,
    { enabled: tab === 'purchases' },
  )
  const returns = useApi(tab === 'returns' ? `/reports/returns${qs}` : null, {
    enabled: tab === 'returns',
  })
  const inventory = useApi(
    tab === 'inventory'
      ? `/reports/inventory${snapshotAt ? `?at=${snapshotAt}` : ''}`
      : null,
    { enabled: tab === 'inventory' },
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reportes"
        subtitle="Ventas, compras, devoluciones e inventario"
        icon={BarChart3}
      />

      {/* Tabs */}
      <div className="inline-flex rounded-xl border border-gray-200 bg-white overflow-hidden">
        {REPORT_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={
              'px-4 py-2 text-sm ' +
              (tab === t.id
                ? 'bg-expo-primary text-white'
                : 'text-expo-dark hover:bg-expo-light/60')
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filtro de periodo */}
      {tab !== 'inventory' ? (
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm text-gray-600">
            Desde:{' '}
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded-lg border border-gray-200 px-2 py-1 text-sm"
            />
          </label>
          <label className="text-sm text-gray-600">
            Hasta:{' '}
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-lg border border-gray-200 px-2 py-1 text-sm"
            />
          </label>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm text-gray-600">
            Snapshot al (vacío = ahora):{' '}
            <input
              type="date"
              value={snapshotAt}
              onChange={(e) => setSnapshotAt(e.target.value)}
              className="rounded-lg border border-gray-200 px-2 py-1 text-sm"
            />
          </label>
        </div>
      )}

      {/* Ventas por cliente */}
      {tab === 'sales' && (
        <Card>
          <CardContent>
            <h3 className="text-lg font-semibold text-expo-dark mb-4">
              Ventas por cliente
            </h3>
            {sales.loading && <p className="text-gray-500">Cargando…</p>}
            {sales.error && <p className="text-red-600">{sales.error.message}</p>}
            {sales.data && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase text-gray-500 border-b">
                    <th className="py-2">Cliente</th>
                    <th className="py-2 text-right">Tallos</th>
                    <th className="py-2 text-right">Valor (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(sales.data.byClient || {}).map(([id, v]) => (
                    <tr key={id} className="border-b border-gray-100 last:border-0">
                      <td className="py-2 text-gray-700">{v.name || id}</td>
                      <td className="py-2 text-right">{fmtQty(v.stems)}</td>
                      <td className="py-2 text-right font-semibold text-expo-dark">
                        {fmtMoney(v.usd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Compras por proveedor */}
      {tab === 'purchases' && (
        <Card>
          <CardContent>
            <h3 className="text-lg font-semibold text-expo-dark mb-4">
              Compras por proveedor
            </h3>
            {purchases.loading && <p className="text-gray-500">Cargando…</p>}
            {purchases.error && (
              <p className="text-red-600">{purchases.error.message}</p>
            )}
            {purchases.data && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase text-gray-500 border-b">
                    <th className="py-2">Proveedor</th>
                    <th className="py-2 text-right">Tallos</th>
                    <th className="py-2 text-right">Valor USD</th>
                    <th className="py-2 text-right">Valor COP</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(purchases.data.bySupplier || {}).map(
                    ([id, v]) => (
                      <tr key={id} className="border-b border-gray-100 last:border-0">
                        <td className="py-2 text-gray-700">{v.name || id}</td>
                        <td className="py-2 text-right">{fmtQty(v.stems)}</td>
                        <td className="py-2 text-right">{fmtMoney(v.usd)}</td>
                        <td className="py-2 text-right">
                          {Number(v.cop || 0).toLocaleString('es-CO')}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Devoluciones */}
      {tab === 'returns' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardContent>
              <h3 className="text-lg font-semibold text-expo-dark mb-4">
                Devoluciones de compra (por proveedor)
              </h3>
              {returns.loading && <p className="text-gray-500">Cargando…</p>}
              {returns.data && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase text-gray-500 border-b">
                      <th className="py-2">Proveedor</th>
                      <th className="py-2 text-right"># Devoluciones</th>
                      <th className="py-2 text-right">Tallos devueltos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(returns.data.bySupplier || {}).map(
                      ([k, v]) => (
                        <tr key={k} className="border-b border-gray-100 last:border-0">
                          <td className="py-2 text-gray-700">{k}</td>
                          <td className="py-2 text-right">{v.count}</td>
                          <td className="py-2 text-right">{fmtQty(v.stems)}</td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <h3 className="text-lg font-semibold text-expo-dark mb-4">
                Devoluciones de venta (por cliente)
              </h3>
              {returns.data && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase text-gray-500 border-b">
                      <th className="py-2">Cliente</th>
                      <th className="py-2 text-right"># Devoluciones</th>
                      <th className="py-2 text-right">Tallos devueltos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(returns.data.byClient || {}).map(
                      ([k, v]) => (
                        <tr key={k} className="border-b border-gray-100 last:border-0">
                          <td className="py-2 text-gray-700">{k}</td>
                          <td className="py-2 text-right">{v.count}</td>
                          <td className="py-2 text-right">{fmtQty(v.stems)}</td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Inventario */}
      {tab === 'inventory' && (
        <Card>
          <CardContent>
            <h3 className="text-lg font-semibold text-expo-dark mb-4">
              Inventario {snapshotAt ? `al ${snapshotAt}` : '(actual)'}
            </h3>
            {inventory.loading && <p className="text-gray-500">Cargando…</p>}
            {inventory.data && (
              <>
                <div className="flex flex-wrap gap-3 mb-3 text-sm">
                  <span className="rounded-full bg-amber-100 text-amber-800 px-3 py-1">
                    Sin procesar: {fmtQty(inventory.data.totals?.unprocessed)}
                  </span>
                  <span className="rounded-full bg-sky-100 text-sky-900 px-3 py-1">
                    Procesado: {fmtQty(inventory.data.totals?.cold_room)}
                  </span>
                  <span className="rounded-full bg-gray-100 text-gray-700 px-3 py-1">
                    Total: {fmtQty(inventory.data.totals?.total)}
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase text-gray-500 border-b">
                      <th className="py-2">Variedad</th>
                      <th className="py-2">Longitud</th>
                      <th className="py-2 text-right">Sin procesar</th>
                      <th className="py-2 text-right">Procesado</th>
                      <th className="py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(inventory.data.items || [])
                      .filter(
                        (r) =>
                          Math.max(0, r.stages.unprocessed) +
                            Math.max(0, r.stages.cold_room) >
                          0,
                      )
                      .map((r) => {
                        const total =
                          Math.max(0, r.stages.unprocessed) +
                          Math.max(0, r.stages.cold_room)
                        return (
                          <tr
                            key={r.id}
                            className="border-b border-gray-100 last:border-0"
                          >
                            <td className="py-2 text-gray-700">{r.variety}</td>
                            <td className="py-2 text-gray-700">
                              {r.stemLength}cm
                            </td>
                            <td className="py-2 text-right">
                              {fmtQty(Math.max(0, r.stages.unprocessed))}
                            </td>
                            <td className="py-2 text-right">
                              {fmtQty(Math.max(0, r.stages.cold_room))}
                            </td>
                            <td className="py-2 text-right font-semibold">
                              {fmtQty(total)}
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
